import React from 'react';
import { listCompanies, listProfiles } from './firestore';
import { formatPersonName } from './names';
import type { Company, UserProfile } from './types';

export interface MemberDirectory {
  profilesById: Map<string, UserProfile>;
  companiesById: Map<string, Company>;
}

const EMPTY_DIRECTORY: MemberDirectory = {
  profilesById: new Map(),
  companiesById: new Map()
};

export function memberPublicProfileUrl(profileId: string) {
  const id = encodeURIComponent(String(profileId || '').trim());
  return `https://sytelineusers.com/Sys/PublicProfile/${id}/`;
}

export function memberLabel(
  profileId: string | undefined,
  fallbackDisplayName: string | undefined,
  directory: MemberDirectory
) {
  const id = String(profileId || '').trim();
  const profile = id ? directory.profilesById.get(id) : null;
  const name = formatPersonName(profile?.displayName || fallbackDisplayName) || (id ? `Member ${id}` : 'Member');
  const companyId = String(profile?.companyId || '').trim();
  const companyName = companyId ? String(directory.companiesById.get(companyId)?.name || '').trim() : '';
  return companyName ? `${name} [${companyName}]` : name;
}

export function useMemberDirectory() {
  const [directory, setDirectory] = React.useState<MemberDirectory>(EMPTY_DIRECTORY);

  React.useEffect(() => {
    let active = true;

    void Promise.all([listProfiles(), listCompanies()])
      .then(([profiles, companies]) => {
        if (!active) return;
        setDirectory({
          profilesById: new Map(profiles.map((profile) => [profile.id, profile])),
          companiesById: new Map(companies.map((company) => [company.id, company]))
        });
      })
      .catch(() => {
        if (active) setDirectory(EMPTY_DIRECTORY);
      });

    return () => {
      active = false;
    };
  }, []);

  return directory;
}
